import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import { DateService } from '../utils/dateService';
import timezoneService from '../services/timezoneService';
import { format } from 'date-fns-tz';
import { calculateDurationsWithGapDetection, formatDuration as formatDurationUtil } from '../utils/durationCalculator';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  Chip,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Search,
  FilterList,
  Delete,
  Download,
  Close,
  Schedule,
  Work,
  Computer,
  ExpandMore,
  CalendarToday,
  PhotoCamera,
  AccessTime,
  Storage,
  Cloud,
} from '@mui/icons-material';

interface Screenshot {
  id: string;
  timestamp: Date;
  thumbnail: string;
  fullImage: string;
  project?: string;
  activity: string;
  confidence: number;
  duration: number; // minutes
  tags: string[];
  aiAnalysis: {
    detectedApps: string[];
    workType: string;
    productivity: 'high' | 'medium' | 'low';
    description: string;
    metadata?: {
      context?: {
        timestamp: string;
        activity: string;
        project: string;
        file?: string;
        platform: string;
        environment: string;
      };
      technical?: {
        url: string;
        title: string;
        browser: string;
        screen: string;
        window: string;
        scroll?: { x: number; y: number };
      };
      focus?: {
        activeElement?: string;
        focusedInput?: string;
      };
    };
  };
}

interface ScreenshotHistoryProps {}

const ScreenshotHistory: React.FC<ScreenshotHistoryProps> = () => {
  const [screenshots, setScreenshots] = useState<Screenshot[]>([]);
  const [filteredScreenshots, setFilteredScreenshots] = useState<Screenshot[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedProject, setSelectedProject] = useState('');
  const [selectedScreenshot, setSelectedScreenshot] = useState<Screenshot | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadedDays, setLoadedDays] = useState(3); // Track how many days we've loaded
  const [hasMore, setHasMore] = useState(true); // Track if there are more screenshots to load
  const [projectSelectionOpen, setProjectSelectionOpen] = useState(false);
  const [suggestedProjects, setSuggestedProjects] = useState<any[]>([]);
  const [suggestedTimeEntries, setSuggestedTimeEntries] = useState<any[]>([]);
  const [existingProjects, setExistingProjects] = useState<any[]>([]);
  const [existingTasks, setExistingTasks] = useState<string[]>([]);
  const [selectedProjectOption, setSelectedProjectOption] = useState<string>('');
  const [userInterval, setUserInterval] = useState<number>(5); // Default 5 minutes

  // Load screenshots from API - initial load of 3 days
  useEffect(() => {
    loadUserInterval();
    loadScreenshots(0, 3, false);
  }, []);

  // Load user's screenshot interval from AI settings
  const loadUserInterval = async () => {
    try {
      const settings = await apiService.getAISettings();
      const interval = settings.data?.screenshotInterval || 5;
      setUserInterval(interval);
      console.log(`⚙️ ScreenshotHistory: Loaded user interval ${interval} minutes`);
    } catch (error) {
      console.error('Failed to load user interval, using default 5 minutes:', error);
      setUserInterval(5);
    }
  };

  const handleLoadMore = async () => {
    const nextDays = loadedDays + 3; // Load 3 more days
    setLoadedDays(nextDays);
    // Load ONLY the next 7 days (from loadedDays to nextDays) and append
    await loadScreenshots(loadedDays, nextDays, true);
  };

  const loadScreenshots = async (startDaysAgo: number = 0, endDaysAgo: number = 7, append: boolean = false) => {
    try {
      setLoading(true);
      
      try {
        // Calculate date range based on day ranges using user's timezone
        // startDaysAgo=0, endDaysAgo=3 → last 3 days (today + 2 previous)
        // startDaysAgo=3, endDaysAgo=6 → days 3-6 ago
        
        // Get current date in user's timezone
        const todayUserTZ = timezoneService.getCurrentUserDate(); // YYYY-MM-DD in user TZ
        
        // Calculate end date (today - startDaysAgo)
        // eslint-disable-next-line no-restricted-syntax
        const endDate = new Date(`${todayUserTZ}T00:00:00`);
        endDate.setDate(endDate.getDate() - startDaysAgo);
        const endDateKey = format(endDate, 'yyyy-MM-dd');
        
        // Calculate start date (today - endDaysAgo + 1)
        // eslint-disable-next-line no-restricted-syntax
        const startDate = new Date(`${todayUserTZ}T00:00:00`);
        startDate.setDate(startDate.getDate() - endDaysAgo + 1);
        const startDateKey = format(startDate, 'yyyy-MM-dd');
        
        // Convert end date to UTC range to cover full local day
        const { endUTC } = DateService.userDateToUTCRange(endDateKey);
        
        console.log(`📸 Frontend will filter screenshots from ${startDateKey} to ${endDateKey} (${endDaysAgo - startDaysAgo} days)`, {
          userTimezone: timezoneService.getUserTimezone(),
          todayUserTZ,
          startDateKey,
          endDateKey,
          endUTC,
          append
        });
        
        // Backend returns screenshots filtered by date range (coarse UTC filter)
        const response = await apiService.getScreenshots({
          startDate: startDateKey,
          endDate: endUTC, // Use UTC end time to cover full local day
          limit: 300 // Load ~3 days worth of screenshots
          // Backend will use default limit of 200 per sorting rules
        } as any);
        
        const data = response.data;
        console.log(`📸 Backend returned ${data.length} total screenshots (${response.pagination.total} total in DB)`);
        
        // FRONTEND FILTERING: Filter by date range in user's timezone
        const filteredData = data.filter((screenshot: any) => {
          // Convert UTC timestamp to user's local time
          const zonedDate = timezoneService.convertUTCToUserTime(screenshot.timestamp);
          
          // Get local date key (YYYY-MM-DD in user's timezone)
          const screenshotDateKey = format(zonedDate, 'yyyy-MM-dd', { 
            timeZone: timezoneService.getUserTimezone() 
          });
          
          // Check if screenshot date is within range
          const isInRange = screenshotDateKey >= startDateKey && screenshotDateKey <= endDateKey;
          
          return isInRange;
        });
        
        console.log(`📸 After frontend filtering: ${filteredData.length} screenshots match date range ${startDateKey} to ${endDateKey}`);
        
        // NEW: Calculate durations using smart gap detection
        const screenshotsWithDuration = calculateDurationsWithGapDetection(filteredData, userInterval);
        
        console.log(`⏱️ ScreenshotHistory: Applied gap detection with interval ${userInterval}min`);
        
        // Transform filtered API data to component format
        const transformedScreenshots: Screenshot[] = screenshotsWithDuration.map((item: any) => {
          // Extract metadata from the aiAnalysis object that now comes directly from backend
          let metadata = null;
          try {
            // Backend now sends aiAnalysis directly as an object
            if (item.aiAnalysis && typeof item.aiAnalysis === 'object') {
              metadata = item.aiAnalysis.metadata || null;
            } else if (item.aiAnalysis && typeof item.aiAnalysis === 'string') {
              const parsed = JSON.parse(item.aiAnalysis);
              metadata = parsed.metadata || null;
            }
          } catch (e) {
            console.warn('Failed to parse AI analysis metadata for screenshot:', item.id);
          }

          const transformedItem = {
            id: item.id,
            // eslint-disable-next-line no-restricted-syntax
            timestamp: new Date(item.timestamp), // Parse UTC timestamp from API
            thumbnail: item.thumbnail,
            fullImage: item.fullImage,
            project: item.projectName,
            activity: item.activity || 'Unknown Activity',
            confidence: item.confidence || 0,
            duration: item.calculatedDuration || item.duration || 0, // Use calculated duration!
            tags: item.tags || [],
            aiAnalysis: {
              detectedApps: item.detectedApplications || [],
              workType: item.workType || 'Unknown',
              productivity: item.productivityLevel || 'medium',
              description: item.analysis || 'No analysis available',
              metadata: metadata
            }
          };
          
          return transformedItem;
        });
        
        if (append) {
          // Append to existing screenshots
          setScreenshots(prev => [...prev, ...transformedScreenshots]);
          setFilteredScreenshots(prev => [...prev, ...transformedScreenshots]);
        } else {
          // Replace screenshots
          setScreenshots(transformedScreenshots);
          setFilteredScreenshots(transformedScreenshots);
        }
        
        // Check if we got fewer screenshots than expected (means no more data)
        setHasMore(transformedScreenshots.length > 0);
        
        console.log('Loaded screenshots from API:', transformedScreenshots.length);
        return;
      } catch (apiError) {
        console.error('API failed, using fallback data:', apiError);
      }
      
      // Show empty state if API fails
      console.warn('API failed, showing empty screenshot list');
      if (!append) {
        setScreenshots([]);
        setFilteredScreenshots([]);
      }
    } finally {
      setLoading(false);
    }
  };

  // Filter screenshots based on search and filters
  useEffect(() => {
    let filtered = screenshots;

    if (searchTerm) {
      filtered = filtered.filter(screenshot => {
        const projectName = screenshot.project || screenshot.aiAnalysis.metadata?.context?.project;
        return screenshot.activity.toLowerCase().includes(searchTerm.toLowerCase()) ||
               projectName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
               screenshot.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
      });
    }

    if (selectedDate) {
      // Convert selectedDate (YYYY-MM-DD from date input) to standard format and compare
      const selectedDateKey = selectedDate; // Already in YYYY-MM-DD format from date input
      filtered = filtered.filter(screenshot => {
        const screenshotDateKey = DateService.toStandardDate(screenshot.timestamp);
        return screenshotDateKey === selectedDateKey;
      });
    }

    if (selectedProject) {
      filtered = filtered.filter(screenshot => {
        const projectName = screenshot.project || screenshot.aiAnalysis.metadata?.context?.project;
        return projectName === selectedProject;
      });
    }

    setFilteredScreenshots(filtered);
  }, [screenshots, searchTerm, selectedDate, selectedProject]);

  const handleViewScreenshot = (screenshot: Screenshot) => {
    setSelectedScreenshot(screenshot);
    setViewDialogOpen(true);
  };

  const handleDeleteScreenshot = (screenshotId: string) => {
    setScreenshots(prev => prev.filter(s => s.id !== screenshotId));
  };

  const handleGenerateTimeEntries = async () => {
    try {
      setLoading(true);
      
      if (!selectedDate) {
        alert('Please select a date first using the date filter above, or use the "Generate Time" button for a specific day below.');
        return;
      }
      
      await handleGenerateTimeEntriesForDate(selectedDate);
    } catch (error) {
      console.error('Failed to get suggested time entries:', error);
      alert('Failed to get suggested time entries. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateTimeEntriesForDate = async (dateString: string) => {
    try {
      setLoading(true);
      
      // STANDARDIZED DATE HANDLING: Convert display date to standard format
      const standardDate = DateService.displayToStandardDate(dateString);
      DateService.debugDateConversion(dateString, 'ScreenshotHistory.handleGenerateTimeEntriesForDate');
      
      console.log('🤖 Getting suggested time entries for date:', standardDate, 'from display date:', dateString);
      
      // Store target date for use in handleCreateTimeEntries
      (window as any).__targetDate = standardDate;
      
      // Get suggested time entries from AI (preview mode)
      const suggestionsRaw = await apiService.getSuggestedTimeEntries(standardDate);
      console.log('🤖 Backend returned:', suggestionsRaw.screenshotsAnalyzed, 'screenshots (ALL unprocessed)');
      
      // FRONTEND TIMEZONE FILTERING: Filter to only screenshots from target date in user's timezone
      let suggestions = suggestionsRaw; // Default to raw suggestions
      
      if (suggestionsRaw.suggestedEntries && suggestionsRaw.suggestedEntries.length > 0) {
        // Get all screenshot IDs from suggested entries
        const allScreenshotIds = new Set(
          suggestionsRaw.suggestedEntries.flatMap((e: any) => e.screenshotIds || [])
        );
        
        // Fetch screenshot data to check timestamps
        const allScreenshotsResponse = await apiService.getScreenshots({ limit: 999 } as any);
        const allScreenshotsData = allScreenshotsResponse.data;
        
        // Filter screenshots by target date in user's timezone
        const validScreenshotIds = new Set(
          allScreenshotsData
            .filter((ss: any) => {
              const zonedDate = timezoneService.convertUTCToUserTime(ss.timestamp);
              const dateKey = format(zonedDate, 'yyyy-MM-dd');
              return dateKey === standardDate;
            })
            .map((ss: any) => ss.id)
        );
        
        console.log(`📸 Frontend filtered: ${validScreenshotIds.size}/${allScreenshotIds.size} screenshots match ${standardDate} in user timezone`);
        
        // Filter and rebuild suggested entries to only include valid screenshots
        const filteredEntries = suggestionsRaw.suggestedEntries
          .map((entry: any) => {
            const validIds = entry.screenshotIds.filter((id: string) => 
              validScreenshotIds.has(id)
            );
            
            if (validIds.length === 0) return null; // Skip entries with no valid screenshots
            
            // Recalculate duration based on valid screenshots only
            const newDuration = Math.max(validIds.length * 5, 15);
            
            // Get the actual screenshot objects for the valid IDs to calculate correct times
            const validScreenshots = allScreenshotsData.filter((ss: any) => 
              validIds.includes(ss.id)
            );
            
            // Sort by timestamp to get first and last
            validScreenshots.sort((a: any, b: any) => 
              new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
            );
            
            const firstScreenshot = validScreenshots[0];
            const lastScreenshot = validScreenshots[validScreenshots.length - 1];
            
            // Calculate new start and end times from filtered screenshots
            const newStartTime = firstScreenshot.timestamp;
            const newEndTime = new Date(new Date(firstScreenshot.timestamp).getTime() + newDuration * 60000).toISOString();
            
            // Extract activities from filtered screenshots
            const screenshotActivities: string[] = [];
            const allDetectedApps = new Set<string>();
            
            validScreenshots.forEach((ss: any) => {
              try {
                const analysis = ss.aiAnalysis || (ss.ai_analysis ? JSON.parse(ss.ai_analysis) : {});
                if (ss.activity) {
                  screenshotActivities.push(ss.activity);
                }
                if (analysis.detectedApps && Array.isArray(analysis.detectedApps)) {
                  analysis.detectedApps.forEach((app: string) => allDetectedApps.add(app));
                }
              } catch (e) {
                // Skip invalid data
              }
            });
            
            // Regenerate notes with correct numbers and times
            const startTimeFormatted = timezoneService.formatUserTime(
              timezoneService.convertUTCToUserTime(newStartTime), 
              { hour: '2-digit', minute: '2-digit' }
            );
            const lastTimeFormatted = timezoneService.formatUserTime(
              timezoneService.convertUTCToUserTime(lastScreenshot.timestamp), 
              { hour: '2-digit', minute: '2-digit' }
            );
            
            const newNotes = `Generated from ${validIds.length} screenshots between ${startTimeFormatted} and ${lastTimeFormatted}.\n\nScreenshot activities:\n${screenshotActivities.slice(0, 3).join('\n')}${screenshotActivities.length > 3 ? `\n... and ${screenshotActivities.length - 3} more` : ''}\n\nApps used: ${Array.from(allDetectedApps).join(', ')}`;
            
            return {
              ...entry,
              screenshotIds: validIds,
              screenshotCount: validIds.length,
              duration: newDuration,
              startTime: newStartTime,
              endTime: newEndTime,
              detectedApps: Array.from(allDetectedApps),
              notes: newNotes
            };
          })
          .filter((e: any) => e !== null);
        
        suggestions = {
          ...suggestionsRaw,
          suggestedEntries: filteredEntries,
          screenshotsAnalyzed: validScreenshotIds.size
        };
        
        console.log(`✅ Frontend filtered suggestions: ${suggestions.suggestedEntries.length} entries, ${suggestions.screenshotsAnalyzed} screenshots`);
      }
      
      if (suggestions.suggestedEntries && suggestions.suggestedEntries.length > 0) {
        console.log('🤖 First suggested entry:', JSON.stringify(suggestions.suggestedEntries[0], null, 2));
        console.log('🤖 All suggested entries:', suggestions.suggestedEntries.map((e: any) => ({
          id: e.id,
          projectName: e.suggestedProjectName,
          duration: e.duration,
          startTime: e.startTime
        })));
        
        // Load existing projects for selection
        const projects = await apiService.getProjects();
        console.log('🤖 Loaded existing projects:', projects);
        setExistingProjects(projects);
        
        // Process suggestions and auto-assign matching projects
        const processedEntries = suggestions.suggestedEntries.map((entry: any) => {
          if (entry.suggestedProjectName) {
            // Sort projects by creation date (newest first)
            const sortedProjects = [...projects].sort((a, b) => {
              const dateA = new Date(a.createdAt || a.created_at || 0);
              const dateB = new Date(b.createdAt || b.created_at || 0);
              return dateB.getTime() - dateA.getTime();
            });
            
            const matchingProject = sortedProjects.find(p => {
              const projectName = p.name.toLowerCase();
              const suggestedName = entry.suggestedProjectName.toLowerCase();
              const workType = entry.workType ? entry.workType.toLowerCase() : '';
              
              // Enhanced matching logic
              if (projectName.includes(suggestedName) || suggestedName.includes(projectName)) {
                return true;
              }
              
              if (workType && projectName.includes(workType)) {
                return true;
              }
              
              if (suggestedName.includes('tracker') && projectName.includes('tracker')) {
                return true;
              }
              
              return false;
            });
            
            if (matchingProject) {
              console.log('🎯 Auto-assigned project:', matchingProject.name, 'to entry:', entry.suggestedProjectName);
              return { ...entry, selectedProjectId: matchingProject.id };
            } else {
              // Default to creating new project
              return { ...entry, selectedProjectId: `new_${entry.suggestedProjectName.toLowerCase().replace(/\s+/g, '_')}` };
            }
          }
          return entry;
        });
        
        setSuggestedTimeEntries(processedEntries);
        
        // Load existing tasks from time entries
        const timeEntries = await apiService.getTimeEntries();
        console.log('Loaded time entries:', timeEntries);
        const uniqueTasks = Array.from(new Set(
          timeEntries
            .map((entry: any) => entry.task || entry.description)
            .filter((task: string) => task && task.trim() !== '')
        ));
        console.log('Unique tasks found:', uniqueTasks);
        setExistingTasks(uniqueTasks);
        
        // Open project selection dialog
        setProjectSelectionOpen(true);
      } else {
        alert('No time entries could be generated from screenshots for the selected date.');
      }
    } catch (error) {
      console.error('Failed to get suggested time entries:', error);
      alert('Failed to get suggested time entries. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadProjectSuggestions = async () => {
    try {
      // Load existing projects
      const projects = await apiService.getProjects();
      console.log('Loaded existing projects:', projects);
      setExistingProjects(projects);

      // Generate AI suggestions based on screenshot activities
      const uniqueActivities = Array.from(new Set(screenshots.map(s => s.activity)));
      const uniqueWorkTypes = Array.from(new Set(screenshots.map(s => s.aiAnalysis.workType)));
      
      const suggestions = [
        ...uniqueWorkTypes.map(workType => ({
          id: `new_${workType.toLowerCase()}`,
          name: `${workType} Work`,
          description: `Auto-generated project for ${workType} activities`,
          isNew: true,
          workType
        })),
        ...uniqueActivities.map(activity => ({
          id: `new_${activity.toLowerCase().replace(/\s+/g, '_')}`,
          name: activity,
          description: `Project based on ${activity} activity`,
          isNew: true,
          activity
        }))
      ];

      // Remove duplicates
      const uniqueSuggestions = suggestions.filter((suggestion, index, self) => 
        index === self.findIndex(s => s.name === suggestion.name)
      );

      setSuggestedProjects(uniqueSuggestions.slice(0, 5)); // Limit to 5 suggestions
    } catch (error) {
      console.error('Error loading project suggestions:', error);
    }
  };

  const handleCreateTimeEntries = async () => {
    try {
      setLoading(true);
      
      let createdCount = 0;
      
      // Get target date from context
      const targetDate = (window as any).__targetDate || timezoneService.getCurrentUserDate();
      console.log('📅 Creating time entries for target date:', targetDate);
      
      // Process each suggested time entry
      for (const entry of suggestedTimeEntries) {
        if (!entry.selectedProjectId) continue;
        
        let projectId = entry.selectedProjectId;
        
        // If it's a new project, create it first
        if (entry.selectedProjectId.startsWith('new_')) {
          const newProject = await apiService.createProject({
            name: entry.suggestedProjectName,
            description: `Auto-generated project for ${entry.workType} work`,
            categoryType: entry.workType.toLowerCase()
          });
          projectId = newProject.id;
        }
        
        // FIX: Recalculate timestamps with correct target date using user timezone
        // 1. Convert backend UTC timestamps to user timezone first
        const userStartTime = timezoneService.convertUTCToUserTime(entry.startTime);
        const userEndTime = timezoneService.convertUTCToUserTime(entry.endTime);
        
        // 2. Extract LOCAL time (not UTC!)
        const startHours = userStartTime.getHours();
        const startMinutes = userStartTime.getMinutes();
        const startSeconds = userStartTime.getSeconds();
        const endHours = userEndTime.getHours();
        const endMinutes = userEndTime.getMinutes();
        const endSeconds = userEndTime.getSeconds();
        
        // 3. Build local timestamp with target date + local time
        const localStartTimestamp = `${targetDate}T${String(startHours).padStart(2, '0')}:${String(startMinutes).padStart(2, '0')}:${String(startSeconds).padStart(2, '0')}`;
        const localEndTimestamp = `${targetDate}T${String(endHours).padStart(2, '0')}:${String(endMinutes).padStart(2, '0')}:${String(endSeconds).padStart(2, '0')}`;
        
        // 4. Convert back to UTC for API (convertUserTimeToUTC expects Date objects)
        // eslint-disable-next-line no-restricted-syntax
        const localStartDate = new Date(localStartTimestamp);
        // eslint-disable-next-line no-restricted-syntax
        const localEndDate = new Date(localEndTimestamp);
        const correctedStartTime = timezoneService.convertUserTimeToUTC(localStartDate);
        const correctedEndTime = timezoneService.convertUserTimeToUTC(localEndDate);
        
        console.log('⏰ Timestamp correction (using user timezone):', {
          original: { 
            start: entry.startTime, 
            end: entry.endTime 
          },
          userTime: {
            start: timezoneService.formatUserDateTime(userStartTime.toISOString()),
            end: timezoneService.formatUserDateTime(userEndTime.toISOString())
          },
          corrected: { 
            start: correctedStartTime, 
            end: correctedEndTime 
          },
          targetDate,
          userTimezone: timezoneService.getUserTimezone()
        });
        
        // Create the time entry with corrected timestamps
        await apiService.createTimeEntry({
          projectId: projectId,
          task: entry.selectedTask || entry.workType,
          description: entry.description,
          startTime: correctedStartTime,
          endTime: correctedEndTime,
          duration: entry.duration,
          notes: entry.notes
        });
        
        createdCount++;
      }
      
      // Mark screenshots as processed
      if (createdCount > 0) {
        // Get all screenshot IDs from all entries
        const allScreenshotIds = suggestedTimeEntries.flatMap(entry => entry.screenshotIds);
        
        // Call backend to mark them as processed
        // This will be handled automatically when we create the time entries
      }
      
      // Close dialog and show success
      setProjectSelectionOpen(false);
      alert(`Successfully created ${createdCount} time entries!`);
      
      // Refresh screenshots to remove processed ones
      await loadScreenshots(0, loadedDays, false);
      
    } catch (error) {
      console.error('Failed to create time entries:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      alert(`Failed to create time entries: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const handleResetProcessedScreenshots = async () => {
    if (!window.confirm('This will reset all processed screenshots so they can be used again for time entry generation. Continue?')) {
      return;
    }
    
    try {
      setLoading(true);
      
      // Call backend to reset processed flag on all screenshots
      const response = await fetch(`${process.env.REACT_APP_API_URL || '/api'}/ai/reset-processed-screenshots`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        alert('Successfully reset processed screenshots!');
        // Refresh screenshots to show the newly available ones
        await loadScreenshots(0, loadedDays, false);
      } else {
        throw new Error('Failed to reset processed screenshots');
      }
    } catch (error) {
      console.error('Failed to reset processed screenshots:', error);
      alert('Failed to reset processed screenshots. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleProjectSelection = async () => {
    try {
      setLoading(true);
      
      let projectId = selectedProjectOption;
      
      // If it's a new project suggestion, create it first
      if (selectedProjectOption.startsWith('new_')) {
        const suggestion = suggestedProjects.find(s => s.id === selectedProjectOption);
        if (suggestion) {
          const newProject = await apiService.createProject({
            name: suggestion.name,
            description: suggestion.description,
            categoryType: suggestion.workType?.toLowerCase() || 'general'
          });
          projectId = newProject.id;
        }
      }
      
      // Generate time entries with selected project
      const result = await apiService.generateTimeEntriesFromScreenshots(selectedDate, projectId);
      
      // Close dialog and show success
      setProjectSelectionOpen(false);
      alert(`Successfully created ${result.timeEntriesCreated} time entries from ${result.screenshotsProcessed} screenshots!`);
      
      console.log('Time entries generated:', result);
    } catch (error) {
      console.error('Failed to generate time entries:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      alert(`Failed to generate time entries: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const getProductivityColor = (productivity: string) => {
    switch (productivity) {
      case 'high': return '#27ae60';
      case 'medium': return '#f39c12';
      case 'low': return '#e74c3c';
      default: return '#95a5a6';
    }
  };

  const getProductivityLabel = (productivity: string) => {
    switch (productivity) {
      case 'high': return 'High Productivity';
      case 'medium': return 'Medium Productivity';
      case 'low': return 'Low Productivity';
      default: return 'Unknown';
    }
  };

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  const uniqueProjects = Array.from(new Set(
    screenshots.map(s => s.project || s.aiAnalysis.metadata?.context?.project).filter(Boolean)
  ));

  // Group screenshots by LOCAL date (user's timezone)
  const groupScreenshotsByDate = (screenshots: Screenshot[]) => {
    const groups: { [key: string]: Screenshot[] } = {};
    
    screenshots.forEach(screenshot => {
      // Convert UTC timestamp to user's local time
      const zonedDate = timezoneService.convertUTCToUserTime(screenshot.timestamp.toISOString());
      
      // Get local date key (YYYY-MM-DD in user's timezone)
      const dateKey = format(zonedDate, 'yyyy-MM-dd', { 
        timeZone: timezoneService.getUserTimezone() 
      });
      
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(screenshot);
    });

    // Sort groups by date (newest first) and sort screenshots within each group by time (newest first)
    const sortedGroups: { date: string; screenshots: Screenshot[]; totalDuration: number; totalScreenshots: number }[] = [];
    
    // FIX: Sort YYYY-MM-DD strings lexicographically (descending)
    Object.keys(groups)
      .sort((a, b) => b.localeCompare(a))
      .forEach(dateKey => {
        const dayScreenshots = groups[dateKey].sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
        const totalDuration = dayScreenshots.reduce((sum, s) => sum + s.duration, 0);
        
        sortedGroups.push({
          date: dateKey,
          screenshots: dayScreenshots,
          totalDuration,
          totalScreenshots: dayScreenshots.length
        });
      });

    return sortedGroups;
  };

  const groupedScreenshots = groupScreenshotsByDate(filteredScreenshots);

  const formatDateHeader = (dateString: string) => {
    // FIX: Now receives YYYY-MM-DD format, use DateService
    return DateService.formatDateHeader(dateString);
  };

  const styles = {
    container: {
      backgroundColor: 'white',
      minHeight: '100vh',
      padding: '24px'
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      marginBottom: '32px'
    },
    title: {
      fontSize: '28px',
      fontWeight: '600',
      color: '#1a1a1a'
    },
    subtitle: {
      fontSize: '16px',
      color: '#666',
      marginBottom: '24px'
    },
    filterBar: {
      display: 'flex',
      gap: '16px',
      marginBottom: '24px',
      flexWrap: 'wrap' as const,
      alignItems: 'center'
    },
    screenshotsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
      gap: '24px',
      marginBottom: '24px'
    },
    screenshotCard: {
      cursor: 'pointer',
      transition: 'transform 0.2s, box-shadow 0.2s',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
      }
    },
    thumbnail: {
      width: '100%',
      height: '120px',
      objectFit: 'cover' as const,
      borderRadius: '8px 8px 0 0'
    },
    cardContent: {
      padding: '12px !important'
    },
    activityTitle: {
      fontSize: '14px',
      fontWeight: '600',
      marginBottom: '8px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap' as const
    },
    metadata: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      marginBottom: '8px',
      fontSize: '12px',
      color: '#666'
    },
    tags: {
      display: 'flex',
      gap: '4px',
      flexWrap: 'wrap' as const,
      marginBottom: '8px'
    },
    confidenceBar: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      marginTop: '8px'
    },
    confidenceProgress: {
      flex: 1,
      height: '4px',
      backgroundColor: '#f0f0f0',
      borderRadius: '2px',
      overflow: 'hidden'
    },
    dialogImageContainer: {
      display: 'flex',
      justifyContent: 'center',
      marginBottom: '24px'
    },
    dialogImage: {
      maxWidth: '100%',
      maxHeight: '400px',
      borderRadius: '8px'
    },
    dialogDetails: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: '16px'
    }
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <Computer sx={{ fontSize: '32px', color: '#e67e22' }} />
        <h1 style={styles.title}>Screenshot History</h1>
      </div>
      
      <p style={styles.subtitle}>
        Browse and manage your AI-captured screenshots with detailed analysis and insights.
      </p>

      {/* Filter Bar */}
      <div style={styles.filterBar}>
        <TextField
          placeholder="Search screenshots..."
          value={searchTerm}
          onChange={(e: any) => setSearchTerm(e.target.value)}
          size="small"
          sx={{ minWidth: '300px' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
        
        <TextField
          type="date"
          label="Filter by date"
          value={selectedDate}
          onChange={(e: any) => setSelectedDate(e.target.value)}
          size="small"
          sx={{ minWidth: '180px' }}
          InputLabelProps={{ shrink: true }}
        />

        <TextField
          select
          label="Project"
          value={selectedProject}
          onChange={(e: any) => setSelectedProject(e.target.value)}
          size="small"
          sx={{ minWidth: '200px' }}
        >
          <MenuItem value="">All Projects</MenuItem>
          {uniqueProjects.map((project) => (
            <MenuItem key={project} value={project}>
              {project}
            </MenuItem>
          ))}
        </TextField>

        <Button
          variant="outlined"
          startIcon={<FilterList />}
        >
          Filters
        </Button>

        <Button
          variant="contained"
          onClick={() => loadScreenshots(0, loadedDays, false)}
          disabled={loading}
          sx={{
            backgroundColor: '#e67e22',
            '&:hover': {
              backgroundColor: '#d35400'
            }
          }}
        >
          {loading ? 'Loading...' : 'Refresh'}
        </Button>

        <Button
          variant="contained"
          onClick={handleGenerateTimeEntries}
          disabled={loading}
          sx={{
            backgroundColor: '#27ae60',
            '&:hover': {
              backgroundColor: '#229954'
            }
          }}
        >
          Generate Time Entries
        </Button>

        <Button
          variant="outlined"
          onClick={handleResetProcessedScreenshots}
          disabled={loading}
          sx={{
            borderColor: '#f39c12',
            color: '#f39c12',
            '&:hover': {
              backgroundColor: '#f39c12',
              color: 'white'
            }
          }}
        >
          Reset Processed (Test)
        </Button>
      </div>

      {/* Screenshots Grouped by Date */}
      {groupedScreenshots.length > 0 ? (
        <Box sx={{ mb: 4 }}>
          {groupedScreenshots.map((group, groupIndex) => (
            <Accordion 
              key={group.date} 
              defaultExpanded={groupIndex === 0}
              sx={{ 
                mb: 2, 
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                '&:before': { display: 'none' },
                borderRadius: '8px !important',
                overflow: 'hidden'
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMore />}
                sx={{
                  backgroundColor: '#f8f9fa',
                  borderBottom: '1px solid #e9ecef',
                  '& .MuiAccordionSummary-content': {
                    alignItems: 'center',
                    gap: 2
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                  <CalendarToday sx={{ color: '#e67e22', fontSize: '20px' }} />
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: '600', color: '#1a1a1a' }}>
                      {formatDateHeader(group.date)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666', mt: 0.5 }}>
                      {group.totalScreenshots} screenshots • {formatDuration(group.totalDuration)} total
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 2 }}>
                  <Chip
                    icon={<PhotoCamera sx={{ fontSize: '14px !important' }} />}
                    label={group.totalScreenshots}
                    size="small"
                    sx={{ 
                      backgroundColor: '#e67e22', 
                      color: 'white',
                      fontSize: '12px'
                    }}
                  />
                  <Chip
                    icon={<AccessTime sx={{ fontSize: '14px !important' }} />}
                    label={formatDuration(group.totalDuration)}
                    size="small"
                    sx={{ 
                      backgroundColor: '#3498db', 
                      color: 'white',
                      fontSize: '12px'
                    }}
                  />
                  <Button
                    variant="contained"
                    size="small"
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation(); // Prevent accordion from toggling
                      console.log('🔥 Generate Time button clicked for date:', group.date);
                      handleGenerateTimeEntriesForDate(group.date);
                    }}
                    disabled={loading}
                    sx={{
                      backgroundColor: '#27ae60',
                      color: 'white',
                      fontSize: '11px',
                      minWidth: 'auto',
                      px: 1.5,
                      py: 0.5,
                      '&:hover': {
                        backgroundColor: '#229954'
                      }
                    }}
                  >
                    Generate Time
                  </Button>
                </Box>
              </AccordionSummary>
              
              <AccordionDetails sx={{ p: 3 }}>
                <div style={styles.screenshotsGrid}>
                  {group.screenshots.map((screenshot) => (
                    <Card key={screenshot.id} sx={styles.screenshotCard} onClick={() => handleViewScreenshot(screenshot)}>
                      <img
                        src={screenshot.thumbnail}
                        alt={screenshot.activity}
                        style={styles.thumbnail}
                      />
                      <CardContent sx={styles.cardContent}>
                        <Typography sx={styles.activityTitle}>
                          {screenshot.activity}
                        </Typography>
                        
                        <div style={styles.metadata}>
                          <Schedule sx={{ fontSize: '14px' }} />
                          <span>{timezoneService.formatUserTime(screenshot.timestamp, { hour: '2-digit', minute: '2-digit' })}</span>
                          <span>•</span>
                          <span>{formatDuration(screenshot.duration)}</span>
                        </div>

                        {/* Show project from AI analysis (prioritize AI matching over metadata) */}
                        {(screenshot.project || screenshot.aiAnalysis.metadata?.context?.project) && (
                          <div style={styles.metadata}>
                            <Work sx={{ fontSize: '14px' }} />
                            <span>{screenshot.project || screenshot.aiAnalysis.metadata?.context?.project}</span>
                          </div>
                        )}

                        {/* Storage location indicator */}
                        {(() => {
                          const isStoredLocally = screenshot.thumbnail.startsWith('file://') || 
                                                 screenshot.thumbnail.startsWith('data:') || 
                                                 screenshot.thumbnail.includes('localhost') ||
                                                 !screenshot.thumbnail.startsWith('http');
                          
                          return (
                            <div style={{
                              ...styles.metadata,
                              color: isStoredLocally ? '#27ae60' : '#3498db',
                              fontWeight: '500'
                            }}>
                              {isStoredLocally ? (
                                <Storage sx={{ fontSize: '14px' }} />
                              ) : (
                                <Cloud sx={{ fontSize: '14px' }} />
                              )}
                              <span>
                                {isStoredLocally ? 'Stored locally on your computer' : 'Stored on server'}
                              </span>
                            </div>
                          );
                        })()}

                        <div style={styles.tags}>
                          {screenshot.tags.slice(0, 2).map((tag) => (
                            <Chip
                              key={tag}
                              label={tag}
                              size="small"
                              sx={{ fontSize: '10px', height: '20px' }}
                            />
                          ))}
                          {screenshot.tags.length > 2 && (
                            <Chip
                              label={`+${screenshot.tags.length - 2}`}
                              size="small"
                              sx={{ fontSize: '10px', height: '20px' }}
                            />
                          )}
                        </div>

                        <div style={styles.confidenceBar}>
                          <Typography variant="caption" color="textSecondary">
                            {screenshot.confidence}% confidence
                          </Typography>
                          <div style={styles.confidenceProgress}>
                            <div
                              style={{
                                width: `${screenshot.confidence}%`,
                                height: '100%',
                                backgroundColor: screenshot.confidence > 80 ? '#27ae60' : screenshot.confidence > 60 ? '#f39c12' : '#e74c3c'
                              }}
                            />
                          </div>
                        </div>

                        <Chip
                          label={getProductivityLabel(screenshot.aiAnalysis.productivity)}
                          size="small"
                          sx={{
                            marginTop: '8px',
                            backgroundColor: getProductivityColor(screenshot.aiAnalysis.productivity),
                            color: 'white',
                            fontSize: '10px'
                          }}
                        />
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </AccordionDetails>
            </Accordion>
          ))}
          
          {/* Load More Button */}
          {hasMore && (
            <Box sx={{ textAlign: 'center', mt: 4 }}>
              <Button
                variant="outlined"
                size="large"
                onClick={handleLoadMore}
                disabled={loading}
                sx={{
                  borderColor: '#3498db',
                  color: '#3498db',
                  px: 4,
                  py: 1.5,
                  fontSize: '16px',
                  fontWeight: '600',
                  '&:hover': {
                    backgroundColor: '#3498db',
                    color: 'white',
                    borderColor: '#3498db'
                  }
                }}
              >
                {loading ? 'Loading...' : `Load More (${loadedDays} days loaded)`}
              </Button>
              <Typography variant="body2" sx={{ color: '#666', mt: 1 }}>
                Loaded last {loadedDays} days. Click to load 3 more days.
              </Typography>
            </Box>
          )}
        </Box>
      ) : (
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography variant="h6" color="textSecondary">
            No screenshots found
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Try adjusting your search criteria or capture some screenshots first.
          </Typography>
        </Box>
      )}

      {/* Time Entry Preview Dialog */}
      <Dialog
        open={projectSelectionOpen}
        onClose={() => setProjectSelectionOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Review and Create Time Entries
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 3, color: '#666' }}>
            AI has analyzed your screenshots and suggests the following time entries. Review and select projects for each entry:
          </Typography>

          {suggestedTimeEntries.map((entry, index) => (
            <Card key={entry.id} sx={{ mb: 3, border: '1px solid #e0e0e0', borderRadius: 2 }}>
              <CardContent sx={{ pb: 2 }}>
                {/* Header with time and duration */}
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center', 
                  mb: 2,
                  pb: 2,
                  borderBottom: '1px solid #f0f0f0'
                }}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: '700', color: '#1a1a1a', mb: 0.5 }}>
                      {entry.description || entry.workType}
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#666', fontWeight: '500' }}>
                      {timezoneService.formatUserTime(timezoneService.convertUTCToUserTime(entry.startTime), { 
                        hour: '2-digit', 
                        minute: '2-digit'
                      })} - {timezoneService.formatUserTime(timezoneService.convertUTCToUserTime(entry.endTime), { 
                        hour: '2-digit', 
                        minute: '2-digit'
                      })}
                    </Typography>
                  </Box>
                  <Chip 
                    label={`${Math.round(entry.duration)} min`}
                    sx={{ 
                      backgroundColor: '#e3f2fd', 
                      color: '#1976d2',
                      fontWeight: '600'
                    }}
                  />
                </Box>

                {/* Apps and screenshots info */}
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Computer sx={{ fontSize: '16px', color: '#666' }} />
                    <Typography variant="body2" sx={{ color: '#666' }}>
                      <strong>Apps:</strong> {entry.detectedApps.join(', ')}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PhotoCamera sx={{ fontSize: '16px', color: '#666' }} />
                    <Typography variant="body2" sx={{ color: '#666' }}>
                      <strong>From:</strong> {entry.screenshotCount} screenshots
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 'medium' }}>
                    Select Project:
                  </Typography>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    value={entry.selectedProjectId || entry.matchedProjectId || 'undefined'}
                    onChange={(e: any) => {
                      const updatedEntries = [...suggestedTimeEntries];
                      updatedEntries[index].selectedProjectId = e.target.value;
                      setSuggestedTimeEntries(updatedEntries);
                    }}
                    placeholder="Choose project..."
                  >
                    <MenuItem value="">
                      <em>Select a project...</em>
                    </MenuItem>
                    
                    {/* Matched Project - FIRST (if available) */}
                    {entry.matchedProjectId && entry.matchedProjectId !== 'undefined' && (
                      <MenuItem key="matched-project" value={entry.matchedProjectId} sx={{ fontWeight: 'bold', backgroundColor: '#e8f5e9' }}>
                        ✅ {entry.matchedProjectName} (Matched)
                      </MenuItem>
                    )}

                    {/* Undefined Project - SECOND (if this entry is undefined) */}
                    {entry.matchedProjectId === 'undefined' && (
                      <MenuItem key="undefined-project" value="undefined" sx={{ fontWeight: 'bold', backgroundColor: '#fff3e0' }}>
                        ❓ Undefined Project (Unmatched)
                      </MenuItem>
                    )}

                    {/* All Existing Projects - THIRD */}
                    {existingProjects.length > 0 && [
                      <MenuItem key="existing-header" disabled sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                        📁 All Projects
                      </MenuItem>,
                      ...existingProjects.map((project) => (
                        <MenuItem key={project.id} value={project.id}>
                          {project.name}
                        </MenuItem>
                      ))
                    ]}
                  </TextField>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 'medium' }}>
                    Task:
                  </Typography>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    value={entry.selectedTask || entry.workType}
                    onChange={(e: any) => {
                      const updatedEntries = [...suggestedTimeEntries];
                      updatedEntries[index].selectedTask = e.target.value;
                      setSuggestedTimeEntries(updatedEntries);
                    }}
                    SelectProps={{
                      native: false,
                    }}
                  >
                    {/* AI Suggested Task */}
                    <MenuItem disabled sx={{ fontWeight: 'bold', backgroundColor: '#f0f0f0' }}>
                      🤖 AI Suggestion
                    </MenuItem>
                    <MenuItem value={entry.workType}>
                      {entry.workType}
                    </MenuItem>
                    
                    {/* Existing Tasks */}
                    {existingTasks.length > 0 && [
                      <MenuItem key="tasks-header" disabled sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                        📋 Existing Tasks
                      </MenuItem>,
                      ...existingTasks.map((task) => (
                        <MenuItem key={task} value={task}>
                          {task}
                        </MenuItem>
                      ))
                    ]}
                  </TextField>
                </Box>

                <Box sx={{ backgroundColor: '#f9f9f9', p: 2, borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 1 }}>
                    Notes:
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.875rem', whiteSpace: 'pre-line' }}>
                    {entry.notes}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          ))}

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
            <Button
              variant="outlined"
              onClick={() => setProjectSelectionOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleCreateTimeEntries}
              disabled={loading || suggestedTimeEntries.some(entry => !entry.selectedProjectId)}
              sx={{
                backgroundColor: '#27ae60',
                '&:hover': {
                  backgroundColor: '#229954'
                }
              }}
            >
              {loading ? 'Creating...' : `Create ${suggestedTimeEntries.length} Time Entries`}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Screenshot Detail Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        {selectedScreenshot && (
          <>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">{selectedScreenshot.activity}</Typography>
              <IconButton onClick={() => setViewDialogOpen(false)}>
                <Close />
              </IconButton>
            </DialogTitle>
            <DialogContent>
              <div style={styles.dialogImageContainer}>
                <img
                  src={selectedScreenshot.fullImage}
                  alt={selectedScreenshot.activity}
                  style={styles.dialogImage}
                />
              </div>
              
              <div style={styles.dialogDetails}>
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Timestamp
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    {selectedScreenshot.timestamp.toLocaleString()}
                  </Typography>

                  <Typography variant="subtitle2" gutterBottom>
                    Duration
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    {formatDuration(selectedScreenshot.duration)}
                  </Typography>

                  {selectedScreenshot.project && (
                    <>
                      <Typography variant="subtitle2" gutterBottom>
                        Project
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 2 }}>
                        {selectedScreenshot.project}
                      </Typography>
                    </>
                  )}

                  <Typography variant="subtitle2" gutterBottom>
                    AI Confidence
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    {selectedScreenshot.confidence}%
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Detected Applications
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    {selectedScreenshot.aiAnalysis.detectedApps.map((app) => (
                      <Chip key={app} label={app} size="small" sx={{ mr: 1, mb: 1 }} />
                    ))}
                  </Box>

                  <Typography variant="subtitle2" gutterBottom>
                    Work Type
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    {selectedScreenshot.aiAnalysis.workType}
                  </Typography>

                  <Typography variant="subtitle2" gutterBottom>
                    Productivity Level
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Chip
                      label={getProductivityLabel(selectedScreenshot.aiAnalysis.productivity)}
                      sx={{
                        backgroundColor: getProductivityColor(selectedScreenshot.aiAnalysis.productivity),
                        color: 'white'
                      }}
                    />
                  </Box>

                  <Typography variant="subtitle2" gutterBottom>
                    Tags
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    {selectedScreenshot.tags.map((tag) => (
                      <Chip key={tag} label={tag} size="small" sx={{ mr: 1, mb: 1 }} />
                    ))}
                  </Box>
                </Box>
              </div>

              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  AI Analysis
                </Typography>
                <Typography variant="body2" sx={{ mb: 3 }}>
                  {selectedScreenshot.aiAnalysis.description}
                </Typography>

                {/* Metadata Section */}
                {selectedScreenshot.aiAnalysis.metadata && (
                  <Box sx={{ mt: 3, mb: 3 }}>
                    <Typography variant="subtitle2" gutterBottom sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                      📊 System Metadata
                    </Typography>
                    
                    <Box sx={{ 
                      backgroundColor: '#f8f9fa', 
                      p: 2, 
                      borderRadius: 2, 
                      border: '1px solid #e9ecef',
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                      gap: 2
                    }}>
                      {/* Context Information */}
                      {selectedScreenshot.aiAnalysis.metadata.context && (
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1, color: '#495057' }}>
                            🎯 Context
                          </Typography>
                          <Box sx={{ fontSize: '0.875rem', color: '#6c757d' }}>
                            {selectedScreenshot.aiAnalysis.metadata.context.platform && (
                              <Box sx={{ mb: 0.5 }}>
                                <strong>Platform:</strong> {selectedScreenshot.aiAnalysis.metadata.context.platform}
                              </Box>
                            )}
                            {selectedScreenshot.aiAnalysis.metadata.context.environment && (
                              <Box sx={{ mb: 0.5 }}>
                                <strong>Environment:</strong> {selectedScreenshot.aiAnalysis.metadata.context.environment}
                              </Box>
                            )}
                            {selectedScreenshot.aiAnalysis.metadata.context.activity && (
                              <Box sx={{ mb: 0.5 }}>
                                <strong>Activity:</strong> {selectedScreenshot.aiAnalysis.metadata.context.activity}
                              </Box>
                            )}
                            {selectedScreenshot.aiAnalysis.metadata.context.file && (
                              <Box sx={{ mb: 0.5 }}>
                                <strong>File:</strong> {selectedScreenshot.aiAnalysis.metadata.context.file}
                              </Box>
                            )}
                          </Box>
                        </Box>
                      )}

                      {/* Technical Information */}
                      {selectedScreenshot.aiAnalysis.metadata.technical && (
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1, color: '#495057' }}>
                            💻 Technical
                          </Typography>
                          <Box sx={{ fontSize: '0.875rem', color: '#6c757d' }}>
                            {selectedScreenshot.aiAnalysis.metadata.technical.browser && (
                              <Box sx={{ mb: 0.5 }}>
                                <strong>Browser:</strong> {selectedScreenshot.aiAnalysis.metadata.technical.browser}
                              </Box>
                            )}
                            {selectedScreenshot.aiAnalysis.metadata.technical.screen && (
                              <Box sx={{ mb: 0.5 }}>
                                <strong>Screen:</strong> {selectedScreenshot.aiAnalysis.metadata.technical.screen}
                              </Box>
                            )}
                            {selectedScreenshot.aiAnalysis.metadata.technical.window && (
                              <Box sx={{ mb: 0.5 }}>
                                <strong>Window:</strong> {selectedScreenshot.aiAnalysis.metadata.technical.window}
                              </Box>
                            )}
                            {selectedScreenshot.aiAnalysis.metadata.technical.url && (
                              <Box sx={{ mb: 0.5 }}>
                                <strong>URL:</strong> 
                                <Typography component="span" sx={{ 
                                  fontSize: '0.75rem', 
                                  wordBreak: 'break-all',
                                  ml: 0.5,
                                  fontFamily: 'monospace',
                                  backgroundColor: '#e9ecef',
                                  px: 0.5,
                                  borderRadius: 0.5
                                }}>
                                  {selectedScreenshot.aiAnalysis.metadata.technical.url}
                                </Typography>
                              </Box>
                            )}
                            {selectedScreenshot.aiAnalysis.metadata.technical.title && (
                              <Box sx={{ mb: 0.5 }}>
                                <strong>Page Title:</strong> {selectedScreenshot.aiAnalysis.metadata.technical.title}
                              </Box>
                            )}
                          </Box>
                        </Box>
                      )}

                      {/* Focus Information */}
                      {selectedScreenshot.aiAnalysis.metadata.focus && (
                        <Box sx={{ gridColumn: 'span 2' }}>
                          <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1, color: '#495057' }}>
                            🎯 Focus & Interaction
                          </Typography>
                          <Box sx={{ fontSize: '0.875rem', color: '#6c757d' }}>
                            {selectedScreenshot.aiAnalysis.metadata.focus.activeElement && (
                              <Box sx={{ mb: 0.5 }}>
                                <strong>Active Element:</strong> 
                                <Typography component="span" sx={{ 
                                  fontSize: '0.75rem', 
                                  ml: 0.5,
                                  fontFamily: 'monospace',
                                  backgroundColor: '#e9ecef',
                                  px: 0.5,
                                  borderRadius: 0.5,
                                  wordBreak: 'break-all'
                                }}>
                                  {selectedScreenshot.aiAnalysis.metadata.focus.activeElement}
                                </Typography>
                              </Box>
                            )}
                            {selectedScreenshot.aiAnalysis.metadata.focus.focusedInput && (
                              <Box sx={{ mb: 0.5 }}>
                                <strong>Focused Input:</strong> {selectedScreenshot.aiAnalysis.metadata.focus.focusedInput}
                              </Box>
                            )}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </Box>
                )}

                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    startIcon={<Download />}
                    size="small"
                  >
                    Download
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<Delete />}
                    size="small"
                    onClick={() => {
                      handleDeleteScreenshot(selectedScreenshot.id);
                      setViewDialogOpen(false);
                    }}
                  >
                    Delete
                  </Button>
                </Box>
              </Box>
            </DialogContent>
          </>
        )}
      </Dialog>
    </div>
  );
};

export default ScreenshotHistory;
